import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { CrewMerchantUi, ReportableEntity } from 'corso-types';
import { useCallback } from 'react';
import { z } from 'zod';
import api from '~/api';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import { isErrorMessage } from '~/utils/errorResponse';

function useListQueryKey(entity: ReportableEntity) {
  const storeId = useStoreId();
  return ['reportDefinitions', storeId, entity] as const;
}

export function useReportDefinitions(entity: ReportableEntity) {
  const queryKey = useListQueryKey(entity);
  return useQuery({
    queryKey,
    initialData: [],
    queryFn: ({ queryKey: [, storeId, e] }) =>
      api.store(storeId).reports.definitions.list(e),
  });
}

export function useAllReportDefinitions() {
  const storeId = useStoreId();
  return useQueries({
    queries: Object.values(ReportableEntity).map((entity) => ({
      queryKey: ['reportDefinitions', storeId, entity],
      initialData: [],
      queryFn: () => api.store(storeId).reports.definitions.list(entity),
    })),
  });
}

export function useReportDefinition(entity: ReportableEntity, id?: number) {
  const storeId = useStoreId();
  return useQuery({
    enabled: id !== undefined,
    queryKey: ['reportDefinition', storeId, entity, id],
    queryFn: () => api.store(storeId).reports.definitions.get(entity, `${id}`),
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  const storeId = useStoreId();
  return useCallback(
    (entity: ReportableEntity) =>
      queryClient.invalidateQueries({
        queryKey: ['reportDefinitions', storeId, entity],
      }),
    [queryClient, storeId],
  );
}

export function useReportDefinitionUpsert() {
  const storeId = useStoreId();
  const toast = useToast();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: z.infer<typeof CrewMerchantUi.reportDefinitionCreate> & {
      id?: number;
    }) =>
      id !== undefined
        ? api
            .store(storeId)
            .reports.definitions.update(payload.kind, `${id}`, payload)
        : api.store(storeId).reports.definitions.create(payload.kind, payload),
    onSuccess: (_, variables) => invalidate(variables.kind),
    onError: (error) => {
      toast.show(
        isErrorMessage(error)
          ? error.message
          : 'Something went wrong. Please try again.',
      );
    },
  });
}

export function useReportDefinitionRun() {
  const storeId = useStoreId();
  const toast = useToast();
  return useMutation({
    mutationFn: (definitionId: number) =>
      api.store(storeId).reports.runFromDefinition(definitionId),
    onSuccess: () =>
      toast.show(
        'Export started. You will receive an email when the CSV is ready.',
      ),
    onError: (error) => {
      toast.show(
        isErrorMessage(error)
          ? error.message
          : 'Failed to start export. Please try again.',
      );
    },
  });
}

export function useReportDefinitionDelete() {
  const storeId = useStoreId();
  const toast = useToast();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ entity, id }: { entity: ReportableEntity; id: number }) =>
      api.store(storeId).reports.definitions.delete(entity, `${id}`),
    onSuccess: (_, variables) => invalidate(variables.entity),
    onError: (error) => {
      toast.show(
        isErrorMessage(error)
          ? error.message
          : 'Something went wrong. Please try again.',
      );
    },
  });
}
